import * as ex from 'excalibur';

import { Player } from "./player";
import { SelectionManager } from "./selection-manager";
import { Board } from './board';
import { ENEMY_SPEED, RANDOM } from './config';
import { PathNodeComponent } from './path-finding/path-node-component';
import { Cell } from './cell';
import { Unit } from './unit';

export class ComputerPlayer extends Player {
    public active: boolean = false;
    constructor(name: string, private selectionManger: SelectionManager, board: Board) {
        super(name, board);
    }

    findClosestEnemy(unit: Unit): Unit | null {
        const enemyCells = this.board.getUnits()
            .filter(u => u.player !== this)
            .map(u => u.cell)
            .filter(c => !!c) as Cell[];

        const closestCell = this.findClosestCell(unit, enemyCells);
        if (closestCell?.unit) {
            return closestCell.unit;
        }
        return null;
    }

    findValidMoveCells(unit: Unit): Cell[] {
        // pick a random valid move
        let range: PathNodeComponent[] = [];
        if (unit.cell) {
            // find empty or enemy occupied cells
            range = this.board.pathFinder.getRange(unit.cell.pathNode, this.mask, unit.unitConfig.movement).filter(node => {
                const cell = node.owner as Cell;
                return cell.unit?.player !== this;
            });
        }
        return range.map(node => node.owner as Cell);
    }

    findClosestCell(unit: Unit, cells: Cell[]) {
        let closest = cells[0];
        if (closest) {
            let distance = Infinity;
            for (let cell of cells) {
                const cellDistance = cell.pos.squareDistance(unit.cell?.pos ?? ex.vec(0, 0));
                if (cellDistance < distance) {
                    closest = cell;
                    distance = cellDistance;
                }
            }
            return closest;
        }
        return null;
    }

    findAttackableTargets(unit: Unit): Cell[] {
        this.selectionManger.selectUnit(unit, "attack");
        const attackRange = this.selectionManger.findAttackRange(unit);
        return attackRange.filter(node => {
            if (node.owner) {
                const cell = node.owner as Cell;
                if (cell.unit) {
                    return cell.unit.player !== this;
                }
            }
            return false;
        }).map(node => node.owner as Cell);
    }

    async maybeAttack(unit: Unit, closestEnemy: Unit) {
        let attacked = false;
        // possible targets
        const possibleTargets = this.findAttackableTargets(unit);
        if (possibleTargets.length > 0) {
            const currentRange = possibleTargets.map(c => c.pathNode);
            this.selectionManger.showHighlight(currentRange, 'attack');
            await ex.Util.delay(ENEMY_SPEED);

            console.log(`[AI] ${unit.name} attacking ${closestEnemy.name}, targetCell=${closestEnemy.cell ? `(${closestEnemy.cell.x},${closestEnemy.cell.y})` : 'null'}`);
            this.selectionManger.showHighlight([closestEnemy.cell!.pathNode], 'path');
            await ex.Util.delay(ENEMY_SPEED);

            // attack
            await unit.attack(closestEnemy);
            console.log(`[AI] ${unit.name} attack() resolved`);
            attacked = true;
        }

        return attacked;
    }

    override async makeMove(): Promise<boolean> {
        const units = this.board.getUnits().filter(u => u.player === this && u.hasActions());


        for (let unit of units) {
            try {
                console.log(`[AI] ${unit.name} turn start`);
                // wait before moving each unit
                await ex.Util.delay(ENEMY_SPEED);

                let range: PathNodeComponent[] = [];
                if (unit.cell) {
                    range = this.board.pathFinder.getRange(unit.cell.pathNode, this.mask, unit.unitConfig.movement);
                }

                // find valid moves
                let validCells = this.findValidMoveCells(unit);

                // find the closest enemy
                const closestEnemy = this.findClosestEnemy(unit);
                console.log(`[AI] ${unit.name} closestEnemy=${closestEnemy?.name ?? 'none'}`);

                if (closestEnemy) {
                    // attack if right next to enemy
                    console.log(`[AI] ${unit.name} maybeAttack (initial)`);
                    const attacked = await this.maybeAttack(unit, closestEnemy);
                    console.log(`[AI] ${unit.name} maybeAttack (initial) done, attacked=${attacked}`);

                    // find the closest valid move to an enemy
                    const closestCell = this.findClosestCell(closestEnemy, validCells);
                    console.log(`[AI] ${unit.name} closestCell=${closestCell ? `(${closestCell.x},${closestCell.y})` : 'null'}`);

                    // move if did not attack
                    if (!attacked) {
                        this.selectionManger.selectUnit(unit, 'move');
                        await ex.Util.delay(ENEMY_SPEED);

                        const currentPath = this.selectionManger.findPath(closestCell!, range);
                        this.selectionManger.showHighlight(currentPath, 'path')
                        await ex.Util.delay(ENEMY_SPEED);

                        console.log(`[AI] ${unit.name} selectDestinationAndMove start, pathLen=${currentPath.length}`);
                        await this.selectionManger.selectDestinationAndMove(unit, closestCell!);
                        console.log(`[AI] ${unit.name} selectDestinationAndMove done`);
                        await ex.Util.delay(ENEMY_SPEED);

                        // try again to attack
                        console.log(`[AI] ${unit.name} maybeAttack (retry)`);
                        await this.maybeAttack(unit, closestEnemy);
                        console.log(`[AI] ${unit.name} maybeAttack (retry) done`);
                    }

                    this.selectionManger.reset();
                }
                // pass if nothing done
                unit.pass();
                console.log(`[AI] ${unit.name} turn end`);
            } catch (err) {
                console.error(`[AI] ${unit.name} threw an error mid-turn, passing to avoid a stuck turn`, err);
                unit.pass();
            }
        }

        return true;
    }

    lose() {
        this.active = false;
        const computerUnits = this.board.getUnits().filter(u => u.player instanceof ComputerPlayer);
        computerUnits.forEach(u => {
            u.health = 0
            u.cell?.removeUnit(u);
        });
    }
}
